/**
 * PORTED — RO-3-00 / #44398.
 * Origin: `excellent/app/_modes/section-cold-start.tsx`.
 *
 * Changes: `"use client"` dropped; `useRouter` → `useNavigate` from the seam.
 * Everything else — including the whole `SECTION_BLANK_STATES` config, which is
 * the single source of truth for a section's blank-state hero — is verbatim.
 *
 * `usePlayLaunch()` comes from `@excellent/ui` and is host-neutral, so the
 * "Connect a source" CTA that opens the Play onboarding walk works here unchanged.
 */

import { type ReactNode } from "react";
import { useNavigate } from "@excellent/ui/router";
import {
  SectionBlankState,
  type BlankStateProvider,
  usePlayLaunch,
} from "@excellent/ui";
import { Telescope, Users, Wallet, Briefcase, Compass } from "@excellent/ui/icons";

/**
 * SectionColdStart — the app-side wiring of the shared `SectionBlankState` block.
 *
 * The block is presentational (in `@excellent/ui`, which can't import shell
 * chrome); this wrapper supplies the per-section copy + provider set and connects
 * the primary CTA to the Play launcher — so an empty section's "Connect a source"
 * opens the Play onboarding walk focused on that section
 * (`usePlayLaunch().launch({ sectionSlug })` → `onboarding:setup-<slug>`). A slug
 * with no open setup card degrades to the top of the ranked queue (never a dead
 * page).
 *
 * The per-section config here is the single source of truth for a section's
 * blank-state hero (title, one-line why, the apps it works with). Add a section by
 * adding a row; override any field inline (e.g. Customers passes a noun-aware
 * title, or a section wires its own `onManual` inline intake).
 *
 * → docs/design/BLANK-STATE-PATTERN.md · prototype docs/design/prototypes/blank-states
 */

export interface SectionColdStartConfig {
  icon: ReactNode;
  title: string;
  description: string;
  providers: BlankStateProvider[];
  connectLabel: string;
}

/** Per-section blank-state config, keyed by the section slug the Play walk uses. */
export const SECTION_BLANK_STATES: Record<string, SectionColdStartConfig> = {
  marketing: {
    icon: <Telescope />,
    title: "See who's finding you",
    description:
      "Connect a web analytics source and Marketing turns into a live view of sessions, top pages, and how you rank. Nothing here until one's connected.",
    providers: [
      { slug: "plausible", name: "Plausible" },
      { slug: "ga4", name: "Google Analytics" },
      { slug: "fathom", name: "Fathom" },
      { slug: "umami", name: "Umami" },
      { slug: "matomo", name: "Matomo" },
    ],
    connectLabel: "Connect a source",
  },
  sales: {
    icon: <Users />,
    title: "Fill your pipeline",
    description:
      "Connect your CRM and every contact, account, and deal comes across — the board, leads, and forecast come alive. Empty until they're in.",
    providers: [
      { slug: "hubspot", name: "HubSpot" },
      { slug: "salesforce", name: "Salesforce" },
      { slug: "pipedrive", name: "Pipedrive" },
      { slug: "google-contacts", name: "Google Contacts" },
    ],
    connectLabel: "Connect a source",
  },
  finance: {
    icon: <Wallet />,
    title: "Track the money",
    description:
      "Connect your bank and billing and the ledger fills itself in — runway, revenue, and MRR follow. Nothing to show until transactions land.",
    providers: [
      { slug: "stripe", name: "Stripe" },
      { slug: "quickbooks", name: "QuickBooks" },
      { slug: "xero", name: "Xero" },
      { slug: "plaid", name: "Plaid" },
    ],
    connectLabel: "Connect a source",
  },
  hiring: {
    icon: <Briefcase />,
    title: "Build your candidate pipeline",
    description:
      "Connect your ATS and every open role and applicant comes across — pipeline, candidates, and scorecards come online. Empty until they're in.",
    providers: [
      { slug: "greenhouse", name: "Greenhouse" },
      { slug: "lever", name: "Lever" },
      { slug: "ashby", name: "Ashby" },
    ],
    connectLabel: "Connect a source",
  },
};

export interface SectionColdStartProps {
  /** Section slug — keys the config AND the Play setup card (`onboarding:setup-<slug>`). */
  slug: string;
  /** Override the config's title (e.g. Customers uses its lexicon noun). */
  title?: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  providers?: BlankStateProvider[];
  connectLabel?: string;
  /** Secondary "add by hand" action. Omit for a connect-only section. */
  onManual?: () => void;
  manualLabel?: string;
  className?: string;
}

export function SectionColdStart({
  slug,
  title,
  description,
  icon,
  providers,
  connectLabel,
  onManual,
  manualLabel,
  className,
}: SectionColdStartProps) {
  const { launch } = usePlayLaunch();
  const cfg = SECTION_BLANK_STATES[slug];

  return (
    <SectionBlankState
      icon={icon ?? cfg?.icon}
      title={title ?? cfg?.title ?? "Nothing here yet"}
      description={description ?? cfg?.description}
      providers={providers ?? cfg?.providers}
      connectLabel={connectLabel ?? cfg?.connectLabel}
      onConnect={() => launch({ sectionSlug: slug })}
      onManual={onManual}
      manualLabel={manualLabel}
      className={className}
    />
  );
}

/**
 * SectionColdStartDegrade — the centered blank state for a mode-section landing
 * that can't render its real surface yet: the workspace tier of a per-project
 * surface (Marketing analytics with no project selected), or a project missing the
 * section entirely. Instead of a bare "this is per-project" pointer, it shows the
 * section's real cold-start — logos + a "Connect a source" CTA that opens the Play
 * onboarding walk — with a quiet secondary that routes to where the surface lives
 * (a project). A slug with a `SECTION_BLANK_STATES` config gets the rich, branded
 * blank state; any other section falls back to a calm generic one.
 */
export function SectionColdStartDegrade({
  slug,
  label,
  hint,
  projectHref = "/projects",
  projectLabel = "Open in a project",
}: {
  /** Section slug — keys the config + Play card. Omit for a generic surface. */
  slug?: string;
  /** Fallback title when there's no config (e.g. "Performance", "Connections"). */
  label: string;
  /** One muted line — why it's empty / where the surface lives. */
  hint: ReactNode;
  /** Where the real surface lives (a project). */
  projectHref?: string;
  projectLabel?: string;
}) {
  const navigate = useNavigate();
  const { launch } = usePlayLaunch();
  const cfg = slug ? SECTION_BLANK_STATES[slug] : undefined;

  if (slug && cfg) {
    return (
      <SectionColdStart
        slug={slug}
        onManual={() => navigate.push(projectHref)}
        manualLabel={projectLabel}
      />
    );
  }

  return (
    <SectionBlankState
      icon={<Compass />}
      title={label}
      description={hint}
      connectLabel={projectLabel}
      onConnect={() => navigate.push(projectHref)}
    />
  );
}
